import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import rentbanner from "@assets/images/istockphoto-3.jpg";
import { useNavigate } from "react-router-dom";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import HouseOutlinedIcon from "@mui/icons-material/HouseOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[8],
  },
}));

const PriceTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: "#e63946",
}));

const ViewButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
}));

export default function PropertyImageGallery() {
  const navigate = useNavigate();
  // Sample property data
  const properties = [
    {
      id: 1,
      title: "Luxury Villa in Palm Jumeirah",
      description:
        "Beachfront property with stunning sea views and premium amenities",
      price: "AED 12,500,000",
      image: "/path/to/image1.jpg",
    },
    {
      id: 2,
      title: "Modern Apartment in Downtown Dubai",
      description: "High-floor unit with Burj Khalifa views and full amenities",
      price: "AED 3,800,000",
      image: "/path/to/image2.jpg",
    },
    {
      id: 3,
      title: "Luxury Penthouse in Dubai Marina",
      description: "Exclusive penthouse with panoramic marina and sea views",
      price: "AED 15,900,000",
      image: "/path/to/image3.jpg",
    },
  ];
  const marketingTools = [
    {
      id: 1,
      icon: <ImageOutlinedIcon />,
      description:
        "Professional photography, videography and 360 virtual tours",
    },
    {
      id: 2,
      icon: <LanguageOutlinedIcon />,
      description:
        "Powerful digital presence that ensures maximum exposure for your property listing",
    },
    {
      id: 3,
      icon: <ShareOutlinedIcon />,
      description: "Social media posts and advertising (across 8+ channels)",
    },
    {
      id: 4,
      icon: <PhoneAndroidOutlinedIcon />,
      description: "Direct marketing campaigns (across Email, WhatsApp, SMS)",
    },
    {
      id: 5,
      icon: <HouseOutlinedIcon />,
      description:
        "Local and international property portals listings and features",
    },
    {
      id: 6,
      icon: <PublicOutlinedIcon />,
      description: "PR and digital media publications",
    },
    {
      id: 7,
      icon: <EventAvailableOutlinedIcon />,
      description: "Private tours and open houses",
    },
    {
      id: 8,
      icon: <HeadsetMicOutlinedIcon />,
      description: "Call center support",
    },
    {
      id: 9,
      icon: <EventNoteOutlinedIcon />,
      description: "Property events and roadshows",
    },
  ];

  return (
    <Box sx={{ py: 6 }}>
      <Container>
        <Grid container spacing={4}>
          {/* Left Side - Text Content */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ mb: { xs: 4, lg: 0 } }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 2,
                }}
              >
                OUR MARKETING TOOLS
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                Best Real Estate Agency Marketing Dubai
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 700 }}
              >
                Explore our handpicked selection of luxury residential and
                commercial properties in Dubai’s most sought-after locations —
                including Downtown, Palm Jumeirah, Dubai Marina, and Business
                Bay. Every listing is carefully chosen to reflect the pinnacle
                of elegance, comfort, and convenience. <br /> <br /> Each
                property is presented with professional high-resolution
                photography, immersive virtual tours, and comprehensive
                descriptions that showcase distinctive architecture, premium
                interiors, and exceptional amenities.
              </Typography>
            </Box>
            {/* Button below both sections */}
            <Box sx={{ textAlign: "left", mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  textTransform: "none",
                  px: 4,
                  backgroundColor: "#C10E14",
                }}
                onClick={() => navigate("/contact-us")}
              >
                Book Expert Consultation
              </Button>
            </Box>
          </Grid>
          {/* Right Side - Property Cards */}
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                borderRadius: 2,
                p: {
                  xs: 2, // padding: 16px
                  sm: 3, // padding: 24px
                  md: 4, // padding: 32px
                  lg: 5, // padding: 40px
                  xl: 6, // padding: 48px
                },
              }}
            >
              <img
                src={rentbanner} // Replace this with your image path
                alt="Real estate marketing"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 5 }}>
          <Grid container spacing={3}>
            {marketingTools.map((tool, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    p: 3,
                    transition: "all 0.3s ease-in-out",
                    boxShadow: "0 4px 20px rgba(247, 130, 134, 0.1)", // Lighter red shadow

                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(247, 130, 134, 0.3)",
                      transform: "translateY(-4px)", // subtle lift
                      borderColor: "#C10E14", // optional border highlight on hover
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        // width: "3em",
                        height: "3em",
                        color: "#C10E14",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {React.cloneElement(tool.icon, {
                        sx: { fontSize: "3em" },
                      })}
                    </Box>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 400,
                      color: "#666",
                    }}
                  >
                    {tool?.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
